import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { trigger, style, transition, animate } from '@angular/animations';
import zxcvbn from 'zxcvbn';

@Component({
  selector: 'app-password-strength',
  templateUrl: './password-strength.component.html',
  styleUrls: ['./password-strength.component.css'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-5px)' }),
        animate('300ms ease-in-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-out', style({ opacity: 0, transform: 'translateY(-5px)' }))
      ])
    ])
  ]
})

export class PasswordStrengthComponent implements OnChanges 
{
  @Input() public passwordToCheck: string = '';
  @Input() public isFocused: boolean = false; // Controla si el campo tiene foco
  @Input() public minPasswordLength: number = 12;
  @Input() public maxPasswordLength: number = 100;
  @Input() public minAcceptablePasswordStrength: number = 0;

  @Output() public validPassword = new EventEmitter<boolean>();


  public showPassword: boolean = false;
  public passwordStrength: number = 0;
  public isValidPassword: boolean = false;
  public isPasswordCriteriaPassed: number = 0;

  public passwordCriteria = {
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    specialChar: false
  };

  public barColors: Array<string> = ['#ddd', '#ddd', '#ddd', '#ddd'];
  public message: string = '';
  public messageColor: string = '#ddd';

  public ngOnChanges(changes: SimpleChanges): void
  {
    if (changes['passwordToCheck'])
    {
      this.checkPasswordStrength();
    }
  }

  public checkPasswordStrength(): void
  {
    const password = this.passwordToCheck || '';

    // Usamos zxcvbn para obtener un análisis completo de la contraseña
    const result = zxcvbn(password);

    // La puntuación de zxcvbn va de 0 a 4, por lo que mapeamos esto a nuestra barra
    this.passwordStrength = result.score;

    this.passwordCriteria = {
      length: password.length >= this.minPasswordLength,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[!@#$%^&*()\-_=+{};:,?"|<.>]/.test(password)
    };

    this.isPasswordCriteriaPassed = Object.values(this.passwordCriteria).filter(Boolean).length;

    this.isValidPassword = this.isPasswordCriteriaPassed === 5 && this.passwordStrength >= this.minAcceptablePasswordStrength;
    this.updateStrengthBar();

    this.validPassword.emit(this.isValidPassword);
  }

  public updateStrengthBar(): void
  {
    const colors = ['#ddd', '#ff4d4d', '#ff9933', '#ffcc00', '#2ecc71'];


    this.barColors = this.barColors.map((_, i) =>
      i < this.passwordStrength ? colors[this.passwordStrength] : '#ddd'
    );

    const messages = ['Muy débil', 'Débil', 'Regular', 'Fuerte', 'Muy fuerte'];
    this.message = messages[this.passwordStrength];
    this.messageColor = colors[this.passwordStrength];
  }

  public togglePasswordVisibility(): void
  {
    this.showPassword = !this.showPassword;
  }

  public onFocus(): void
  {
    this.isFocused = true;
  }

  public onBlur(): void
  {
    if (!this.isValidPassword)
    {
      this.isFocused = true;
    }
    else
    {
      this.isFocused = false;
    }
  }

  public resetPasswordStrength(): void
  {
    this.passwordStrength = 0;
    this.passwordCriteria = {
      length: false,
      lowercase: false,
      uppercase: false,
      number: false,
      specialChar: false
    };
    this.message = '';
    this.messageColor = '#ddd';
    this.barColors = ['#ddd', '#ddd', '#ddd', '#ddd'];
  }
}
